import React, { memo, useEffect, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  FiEye,
  FiEdit2,
  FiTrash2,
  FiX,
  FiCheck,
  FiChevronDown
} from "react-icons/fi";
import SmartImage from "../../components/common/SmartImage";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200];
/** Initial row height estimate for the virtualizer (measured per row for variable height) */
const ROW_HEIGHT_ESTIMATE = 88;

/**
 * Shared grid: virtualized rows cannot use <tr style=position:absolute> — it breaks column
 * alignment with <thead>. One grid template for header + body keeps every cell under its column.
 */
const GRID_TEMPLATE_COLUMNS =
  "minmax(0, 1.4fr) minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 0.85fr) minmax(0, 0.65fr) minmax(0, 0.75fr) minmax(0, 0.7fr) minmax(0, 0.85fr)";

function dashText(value) {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

const InfluencerTableRow = memo(function InfluencerTableRow({
  inf,
  style,
  measureRef,
  onView,
  onEdit,
  onToggle,
  onDelete
}) {
  const email = dashText(inf.email);
  const mobile =
    inf.mobile === null || inf.mobile === undefined || inf.mobile === ""
      ? "-"
      : String(inf.mobile);
  const name = dashText(inf.name);
  const usernameRaw =
    inf.username === null || inf.username === undefined || inf.username === ""
      ? null
      : String(inf.username);
  const usernameLine = usernameRaw ? `@${usernameRaw}` : "-";

  const category = dashText(inf.category);
  const hasPrice =
    inf.price !== null && inf.price !== undefined && inf.price !== "";
  const priceDisplay = hasPrice ? `₹${inf.price}` : "-";

  const hasFollowers =
    inf.followers !== null &&
    inf.followers !== undefined &&
    inf.followers !== "";
  const followersDisplay = hasFollowers
    ? Number(inf.followers).toLocaleString()
    : "-";

  const statusIsBool = typeof inf.isActive === "boolean";

  return (
    <div
      ref={measureRef}
      role="row"
      style={{
        ...style,
        display: "grid",
        gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
        alignItems: "stretch"
      }}
      className="border-b border-gray-100 bg-white hover:bg-gray-50 transition"
    >
      <div role="cell" className="px-6 py-4 flex items-center min-w-0">
        <div className="flex items-center min-h-[40px] min-w-0 w-full">
          <SmartImage
            src={inf.avatar || inf.image}
            alt={name === "-" ? "Influencer" : name}
            className="h-10 w-10 rounded-full object-cover shrink-0"
          />
          <div className="ml-4 min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {name}
            </div>
            <div className="text-sm text-gray-500 truncate">{usernameLine}</div>
          </div>
        </div>
      </div>

      <div role="cell" className="px-6 py-4 flex flex-col justify-center min-w-0">
        <div className="text-sm text-gray-900 truncate">{email}</div>
        <div className="text-sm text-gray-500 truncate">{mobile}</div>
      </div>

      <div role="cell" className="px-6 py-4 flex items-center min-w-0">
        <div className="flex flex-wrap gap-1 min-w-0">
          {Array.isArray(inf.platform) && inf.platform.length > 0 ? (
            inf.platform.map((p, i) => (
              <span
                key={i}
                className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full"
              >
                {p}
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-400">-</span>
          )}
        </div>
      </div>

      <div
        role="cell"
        className="px-6 py-4 text-sm text-gray-700 flex items-center min-w-0 truncate"
      >
        {category}
      </div>

      <div
        role="cell"
        className="px-6 py-4 text-sm font-medium text-gray-900 flex items-center whitespace-nowrap min-w-0"
      >
        {priceDisplay}
      </div>

      <div
        role="cell"
        className="px-6 py-4 text-sm text-gray-900 flex items-center whitespace-nowrap min-w-0"
      >
        {followersDisplay}
      </div>

      <div role="cell" className="px-6 py-4 flex items-center min-w-0">
        {statusIsBool ? (
          <span
            className={`inline-flex px-3 py-1 text-xs rounded-full font-medium ${
              inf.isActive
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {inf.isActive ? "Active" : "Inactive"}
          </span>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )}
      </div>

      <div
        role="cell"
        className="px-6 py-4 flex items-center justify-end gap-3 shrink-0 min-w-0"
      >
        <button
          type="button"
          onClick={() => onView(inf)}
          className="text-blue-600 hover:text-blue-800"
          aria-label="View"
        >
          <FiEye size={18} />
        </button>

        <button
          type="button"
          onClick={() => onEdit(inf._id)}
          className="text-orange-600 hover:text-orange-800"
          aria-label="Edit"
        >
          <FiEdit2 size={18} />
        </button>

        <button
          type="button"
          onClick={() => onToggle(inf._id)}
          className={
            inf.isActive
              ? "text-yellow-600 hover:text-yellow-800"
              : "text-green-600 hover:text-green-800"
          }
          aria-label={inf.isActive ? "Deactivate" : "Activate"}
        >
          {inf.isActive ? <FiX size={18} /> : <FiCheck size={18} />}
        </button>

        <button
          type="button"
          onClick={() => onDelete(inf._id)}
          className="text-red-600 hover:text-red-800"
          aria-label="Delete"
        >
          <FiTrash2 size={18} />
        </button>
      </div>
    </div>
  );
});

const InfluencerTable = ({
  influencers,
  pagination,
  loading,
  onPageChange,
  onPageSizeChange,
  onDelete,
  onToggle,
  onEdit,
  onView,
  searchTerm,
  setSearchTerm
}) => {
  const scrollParentRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: influencers.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: 6
  });

  useEffect(() => {
    scrollParentRef.current?.scrollTo({ top: 0 });
  }, [pagination.page, pagination.limit]);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-10 w-10 border-2 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading influencers...</p>
      </div>
    );
  }

  if (!influencers.length) {
    return (
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <div className="max-w-md">
            <label
              htmlFor="influencer-search"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Search Influencers
            </label>
            <div className="flex gap-2">
              <input
                id="influencer-search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by username, name, or email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                disabled={!searchTerm}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
        <div className="p-8 text-center text-gray-500">
          No influencers found.
        </div>
      </div>
    );
  }

  const total = pagination.total ?? influencers.length;
  const limit = pagination.limit ?? PAGE_SIZE_OPTIONS[0];
  const page = pagination.page ?? 1;
  const pages = Math.max(1, pagination.pages ?? 1);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  const pageSizeSelectOptions = PAGE_SIZE_OPTIONS.includes(limit)
    ? PAGE_SIZE_OPTIONS
    : [...PAGE_SIZE_OPTIONS, limit].sort((a, b) => a - b);

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      <div className="p-4 border-b bg-gray-50 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="max-w-md flex-1">
          <label
            htmlFor="influencer-search"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Search Influencers
          </label>
          <div className="flex gap-2">
            <input
              id="influencer-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by username, name, or email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              disabled={!searchTerm}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-1 shrink-0">
          <span className="text-sm font-medium text-gray-700">Rows per page</span>
          <div className="relative inline-flex">
            <select
              aria-label="Rows per page"
              value={limit}
              onChange={(e) =>
                onPageSizeChange?.(Number(e.target.value))
              }
              className="appearance-none pl-3 pr-9 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 cursor-pointer min-w-[5.5rem]"
            >
              {pageSizeSelectOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <FiChevronDown
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
              size={18}
              aria-hidden
            />
          </div>
        </div>
      </div>

      <div
        ref={scrollParentRef}
        className="overflow-x-auto overflow-y-auto max-h-[min(70vh,640px)]"
      >
        <div className="min-w-[960px] w-full" role="table" aria-label="Marketplace influencers">
          <div
            role="row"
            className="grid border-b border-gray-200 bg-gray-50 text-xs uppercase text-gray-500 sticky top-0 z-10 shadow-sm"
            style={{ gridTemplateColumns: GRID_TEMPLATE_COLUMNS }}
          >
            <div role="columnheader" className="px-6 py-3 text-left">
              Influencer
            </div>
            <div role="columnheader" className="px-6 py-3 text-left">
              Contact
            </div>
            <div role="columnheader" className="px-6 py-3 text-left">
              Platform
            </div>
            <div role="columnheader" className="px-6 py-3 text-left">
              Category
            </div>
            <div role="columnheader" className="px-6 py-3 text-left">
              Price
            </div>
            <div role="columnheader" className="px-6 py-3 text-left">
              Followers
            </div>
            <div role="columnheader" className="px-6 py-3 text-left">
              Status
            </div>
            <div role="columnheader" className="px-6 py-3 text-right">
              Actions
            </div>
          </div>

          <div
            role="rowgroup"
            className="relative bg-white"
            style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const inf = influencers[virtualRow.index];
              return (
                <InfluencerTableRow
                  key={inf._id}
                  inf={inf}
                  measureRef={virtualRow.measureElement}
                  onView={onView}
                  onEdit={onEdit}
                  onToggle={onToggle}
                  onDelete={onDelete}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 p-4 bg-gray-50 border-t">
        <p className="text-sm text-gray-600">
          Showing {from}–{to} of {total}
          {pages > 1 ? (
            <>
              {" "}
              · Page {page} of {pages}
            </>
          ) : null}
        </p>

        {pages > 1 ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default memo(InfluencerTable);
